/* Admin calendar — the overlap rules.
 *
 * Two pure functions carry the whole redesign:
 *
 *   buildItems()  merges back-to-back slots of the same bookable into one run,
 *                 so a court that is bookable 06:00–10:00 is one bar instead of
 *                 four slivers.
 *   layout()      assigns lanes inside each overlap group and collects whatever
 *                 doesn't fit into per-hour "+N" buttons.
 *
 * Both are deliberately free of React and of our API shapes (they take CalItem /
 * CalSession) so they can be unit-tested on their own. */

import { CAL_TYPE_ORDER, type CalItem, type CalSession } from './calendarTypes';

// ─── Time helpers ────────────────────────────────────────────────────────────

export const timeToMin = (hhmm: string): number => {
  const [h, m] = (hhmm || '00:00').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

export const minToTime = (min: number): string =>
  `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(Math.round(min) % 60).padStart(2, '0')}`;

// ─── Item building ───────────────────────────────────────────────────────────

/** Merge runs of back-to-back slots of the same bookable on the same day.
 *  A run of one slot stays an ordinary item — a single court hour is just a
 *  session. Cancelled slots take part in a run (they still occupy the grid) and
 *  are drawn as a hatched segment; a gap always ends the run. */
export function buildItems(sessions: CalSession[]): CalItem[] {
  const items: CalItem[] = sessions
    .filter(s => !s.slot)
    .map(s => ({ kind: 'one' as const, id: s.id, start: s.start, end: s.end, type: s.type, s }));

  const slots = sessions
    .filter(s => s.slot)
    .sort((a, b) => a.bk.localeCompare(b.bk) || a.date.localeCompare(b.date) || a.start - b.start || a.id.localeCompare(b.id));

  let cur: Extract<CalItem, { kind: 'run' }> | null = null;
  for (const s of slots) {
    if (cur && cur.bk === s.bk && cur.s.date === s.date && cur.end === s.start) {
      cur.slots.push(s);
      cur.end = s.end;
    } else {
      cur = { kind: 'run', id: `run-${s.id}`, bk: s.bk, day: s.day, start: s.start, end: s.end, type: s.type, s, slots: [s] };
      items.push(cur);
    }
  }

  return items.map(i =>
    i.kind === 'run' && i.slots.length === 1
      ? { kind: 'one' as const, id: i.slots[0].id, start: i.start, end: i.end, type: i.type, s: i.slots[0] }
      : i
  );
}

export interface ItemStats {
  booked: number;
  cap: number;
  wait: number;
  /** How many of the underlying sessions are full. */
  full: number;
  /** How many sessions the item covers (1, unless it is a run). */
  n: number;
  fill: number;
}

/** Totals for an item — a run sums its slots, everything else is its session.
 *  Cancelled sessions contribute nothing: they are not capacity any more. */
export function itemStats(it: CalItem): ItemStats {
  if (it.kind === 'more') return { booked: 0, cap: 0, wait: 0, full: 0, n: it.items.length, fill: 0 };
  const list = it.kind === 'run' ? it.slots : [it.s];
  let booked = 0;
  let cap = 0;
  let wait = 0;
  let full = 0;
  for (const s of list) {
    if (s.status === 'cancelled') continue;
    booked += s.booked;
    cap += s.cap;
    wait += s.wait;
    if (s.booked >= s.cap) full++;
  }
  return { booked, cap, wait, full, n: list.length, fill: cap ? booked / cap : 0 };
}

// ─── Lane layout ─────────────────────────────────────────────────────────────

export interface PlacedItem {
  it: CalItem;
  lane: number;
  lanes: number;
}

/** How many card lanes a column of this width can carry before overlaps stop
 *  being readable. Below ~62px a card shows nothing but a clipped title. */
export const maxLanesFor = (colWidth: number, ceiling = 4): number =>
  Math.max(2, Math.min(ceiling, Math.floor(colWidth / 62)));

/**
 * Greedy lanes with per-hour overflow.
 *
 * Items are grouped into connected runs of overlap. Within a group each item
 * takes the first lane whose previous item has already ended. If the group fits
 * in `maxLanes`, every item gets an equal share of the column. If it doesn't,
 * the first `maxLanes - 1` lanes keep real cards and everything behind them is
 * collected into "+N" buttons — one per start hour, capped to the end of that
 * hour so a long hidden item can't swallow the afternoon. A button holding a
 * single item renders that item instead, because "+1" tells nobody anything.
 */
export function layout(items: CalItem[], maxLanes: number): PlacedItem[] {
  const sorted = [...items].sort(
    (a, b) =>
      a.start - b.start ||
      b.end - b.start - (a.end - a.start) ||
      CAL_TYPE_ORDER.indexOf(typeOf(a)) - CAL_TYPE_ORDER.indexOf(typeOf(b)) ||
      a.id.localeCompare(b.id)
  );

  const out: PlacedItem[] = [];
  let group: Array<CalItem & { _lane?: number }> = [];
  let groupEnd = -1;

  const flush = () => {
    if (!group.length) return;
    const laneEnds: number[] = [];
    for (const it of group) {
      let lane = laneEnds.findIndex(e => e <= it.start);
      if (lane < 0) {
        lane = laneEnds.length;
        laneEnds.push(0);
      }
      laneEnds[lane] = it.end;
      it._lane = lane;
    }
    const lanes = laneEnds.length;

    if (lanes <= Math.max(2, maxLanes)) {
      for (const it of group) out.push({ it, lane: it._lane!, lanes });
    } else {
      const keep = Math.max(1, maxLanes - 1);
      const hidden: CalItem[] = [];
      for (const it of group) {
        if (it._lane! < keep) out.push({ it, lane: it._lane!, lanes: maxLanes });
        else hidden.push(it);
      }
      hidden.sort((a, b) => a.start - b.start);

      const buckets: Array<Extract<CalItem, { kind: 'more' }>> = [];
      let bucket: Extract<CalItem, { kind: 'more' }> | null = null;
      for (const h of hidden) {
        if (bucket && h.start < bucket.end && Math.floor(h.start / 60) === Math.floor(bucket.start / 60)) {
          bucket.items.push(h);
          bucket.end = Math.max(bucket.end, h.end);
        } else {
          bucket = { kind: 'more', id: `more-${h.id}`, start: h.start, end: h.end, items: [h] };
          buckets.push(bucket);
          out.push({ it: bucket, lane: keep, lanes: maxLanes });
        }
      }
      for (const placed of out) {
        if (placed.it.kind !== 'more' || !buckets.includes(placed.it)) continue;
        if (placed.it.items.length === 1) placed.it = placed.it.items[0];
        else placed.it.end = Math.min(placed.it.end, Math.floor(placed.it.start / 60) * 60 + 60);
      }
    }
    group = [];
  };

  for (const it of sorted) {
    if (it.start >= groupEnd) {
      flush();
      groupEnd = it.end;
    } else {
      groupEnd = Math.max(groupEnd, it.end);
    }
    group.push(it);
  }
  flush();

  return out;
}

/** Only real items reach layout(); "+N" buckets are created by it, never fed in. */
function typeOf(i: CalItem) {
  return i.kind === 'more' ? CAL_TYPE_ORDER[0] : i.type;
}
